import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, RadioIcon, BarChart2Icon, MessageSquareIcon, ZapIcon, UsersIcon, LayoutDashboardIcon, StarIcon } from 'lucide-react';
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-457">
      {children}
    </motion.div>;
}
const controlPillars = [{
  letter: 'C',
  title: 'Campaña propia',
  description: 'Tu motor de generación de leads. Configuramos campañas en Meta y Google bajo tu cuenta, con tracking completo y optimización continua.',
  solves: 'Elimina la dependencia de leads prestados o referidos.',
  icon: RadioIcon
}, {
  letter: 'O',
  title: 'Optimización',
  description: 'Datos reales para mejorar cada semana. Revisamos métricas, ajustamos creativos y refinamos audiencias con base en resultados reales.',
  solves: 'Convierte la intuición en decisiones basadas en datos.',
  icon: BarChart2Icon
}, {
  letter: 'N',
  title: 'Nutrición',
  description: 'Secuencias automáticas que mantienen el contacto. WhatsApp + email + SMS para que ningún lead se enfríe por falta de seguimiento.',
  solves: 'Maximiza el valor de cada lead generado.',
  icon: MessageSquareIcon
}, {
  letter: 'T',
  title: 'Tráfico',
  description: 'Canales pagados y orgánicos bajo tu control. Diversificamos las fuentes para que tu flujo no dependa de un solo canal.',
  solves: 'Reduce el riesgo de dependencia de una sola plataforma.',
  icon: ZapIcon
}, {
  letter: 'R',
  title: 'Reclutamiento',
  description: 'Sistema para crecer tu equipo con estructura. Proceso documentado para atraer, evaluar e incorporar nuevos agentes.',
  solves: 'Escala sin que el crecimiento dependa de ti personalmente.',
  icon: UsersIcon
}, {
  letter: 'O',
  title: 'Operación',
  description: 'CRM, dashboards y procesos documentados. Todo centralizado en GHL para que el equipo opere con claridad y sin fricción.',
  solves: 'Elimina el caos operativo y la dependencia del dueño.',
  icon: LayoutDashboardIcon
}, {
  letter: 'L',
  title: 'Liderazgo',
  description: 'Mentoría y comunidad de agentes que ya lo están construyendo. Acceso a sesiones grupales, revisiones individuales y red de pares.',
  solves: 'Acelera el aprendizaje con contexto real del mercado hispano.',
  icon: StarIcon
}];
const flowSteps = [{
  num: '01',
  label: 'Lead Propio',
  description: 'Generado por tu campaña, bajo tu control.'
}, {
  num: '02',
  label: 'Conversación Inicial',
  description: 'Secuencia automática de contacto activa.'
}, {
  num: '03',
  label: 'Cita Agendada',
  description: 'Proceso de calificación y agendamiento.'
}, {
  num: '04',
  label: 'Póliza',
  description: 'Conversión con proceso documentado.'
}, {
  num: '05',
  label: 'Reclutamiento',
  description: 'Clientes satisfechos como candidatos.'
}];
export function ComoFuncionaPage() {
  return <div className="bg-charcoal" data-id="element-458">
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-459">
        <div className="max-w-4xl mx-auto text-center" data-id="element-460">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-461">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold tracking-wide uppercase mb-6" data-id="element-462">
              El método
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-463">
            Así se construye <span className="text-accent" data-id="element-464">CONTROL</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-[#b8b4ae] mb-10 max-w-xl mx-auto" data-id="element-465">
            Un método. Un sistema. Resultados predecibles.
          </motion.p>
        </div>
      </section>

      {/* MÉTODO CONTROL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border-subtle" data-id="element-466">
        <div className="max-w-6xl mx-auto" data-id="element-467">
          <FadeInSection data-id="element-468">
            <div className="text-center mb-12" data-id="element-469">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-470">
                El método CONTROL: 7 palancas
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-471">
                Cada letra representa un sistema que instalamos.
              </p>
            </div>
          </FadeInSection>
          <div className="space-y-4" data-id="element-472">
            {controlPillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <FadeInSection key={pillar.letter + pillar.title} delay={i * 0.07} data-id="element-473">
                  <div className="bg-surface border border-border-subtle rounded-2xl p-6 sm:p-8" data-id="element-474">
                    <div className="flex flex-col sm:flex-row gap-6" data-id="element-475">
                      <div className="flex items-start gap-4 sm:w-64 flex-shrink-0" data-id="element-476">
                        <span className="text-5xl font-black text-accent leading-none" data-id="element-477">
                          {pillar.letter}
                        </span>
                        <div data-id="element-478">
                          <Icon size={18} className="text-[#7a7672] mb-1" data-id="element-479" />
                          <h3 className="font-bold text-cream" data-id="element-480">
                            {pillar.title}
                          </h3>
                        </div>
                      </div>
                      <div className="flex-1" data-id="element-481">
                        <p className="text-[#b8b4ae] leading-relaxed mb-3" data-id="element-482">
                          {pillar.description}
                        </p>
                        <div className="flex items-start gap-2" data-id="element-483">
                          <span className="text-accent text-xs font-semibold flex-shrink-0 mt-0.5" data-id="element-484">
                            →
                          </span>
                          <span className="text-xs text-[#7a7672]" data-id="element-485">
                            {pillar.solves}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeInSection>;
          })}
          </div>
        </div>
      </section>

      {/* DIAGRAMA DE FLUJO */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-486">
        <div className="max-w-5xl mx-auto" data-id="element-487">
          <FadeInSection data-id="element-488">
            <div className="text-center mb-12" data-id="element-489">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-490">
                El flujo completo: de lead a reclutamiento
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-491">
                Cada etapa conectada. Ningún paso improvisado.
              </p>
            </div>
          </FadeInSection>

          {/* Desktop flow */}
          <FadeInSection delay={0.1} data-id="element-492">
            <div className="hidden md:flex items-stretch gap-0" data-id="element-493">
              {flowSteps.map((step, i) => <div key={step.num} className="flex items-center flex-1" data-id="element-494">
                  <div className="flex-1 bg-elevated border border-border-subtle rounded-xl p-5 text-center" data-id="element-495">
                    <span className="text-xs font-bold text-accent block mb-2" data-id="element-496">
                      {step.num}
                    </span>
                    <h3 className="font-semibold text-cream text-sm mb-1.5" data-id="element-497">
                      {step.label}
                    </h3>
                    <p className="text-xs text-[#b8b4ae] leading-relaxed" data-id="element-498">
                      {step.description}
                    </p>
                  </div>
                  {i < flowSteps.length - 1 && <div className="flex items-center px-2 flex-shrink-0" data-id="element-499">
                      <ArrowRightIcon size={16} className="text-accent" data-id="element-500" />
                    </div>}
                </div>)}
            </div>

            {/* Mobile flow */}
            <div className="md:hidden flex flex-col gap-3" data-id="element-501">
              {flowSteps.map((step, i) => <div key={step.num} className="flex flex-col items-center" data-id="element-502">
                  <div className="w-full bg-elevated border border-border-subtle rounded-xl p-5" data-id="element-503">
                    <div className="flex items-center gap-3" data-id="element-504">
                      <span className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent font-bold text-xs flex-shrink-0" data-id="element-505">
                        {step.num}
                      </span>
                      <div data-id="element-506">
                        <h3 className="font-semibold text-cream text-sm" data-id="element-507">
                          {step.label}
                        </h3>
                        <p className="text-xs text-[#b8b4ae]" data-id="element-508">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  {i < flowSteps.length - 1 && <div className="w-px h-4 bg-border-subtle" data-id="element-509" />}
                </div>)}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* EL STACK */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-510">
        <div className="max-w-5xl mx-auto" data-id="element-511">
          <FadeInSection data-id="element-512">
            <div className="text-center mb-12" data-id="element-513">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-514">
                Las herramientas del sistema
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-515">
                Stack probado. Integrado. Bajo tu control.
              </p>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12" data-id="element-516">
            <FadeInSection delay={0.1} data-id="element-517">
              <div className="bg-surface border border-border-subtle rounded-2xl p-7 h-full" data-id="element-518">
                <div className="flex items-center gap-3 mb-4" data-id="element-519">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center" data-id="element-520">
                    <LayoutDashboardIcon size={18} className="text-accent" data-id="element-521" />
                  </div>
                  <h3 className="font-bold text-cream" data-id="element-522">GoHighLevel (GHL)</h3>
                </div>
                <p className="text-[#b8b4ae] text-sm leading-relaxed" data-id="element-523">
                  CRM white-label que centraliza leads, automatizaciones,
                  pipelines y comunicaciones. Tu plataforma, bajo tu marca, con
                  acceso completo para tu equipo.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-524">
              <div className="bg-surface border border-border-subtle rounded-2xl p-7 h-full" data-id="element-525">
                <div className="flex items-center gap-3 mb-4" data-id="element-526">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center" data-id="element-527">
                    <MessageSquareIcon size={18} className="text-accent" data-id="element-528" />
                  </div>
                  <h3 className="font-bold text-cream" data-id="element-529">
                    WhatsApp Business API
                  </h3>
                </div>
                <p className="text-[#b8b4ae] text-sm leading-relaxed" data-id="element-530">
                  Canal de contacto de alta apertura integrado con el CRM para
                  secuencias automáticas. Contacto inmediato, seguimiento
                  estructurado, sin trabajo manual.
                </p>
              </div>
            </FadeInSection>
          </div>

          {/* Stack Diagram */}
          <FadeInSection delay={0.2} data-id="element-531">
            <div className="bg-surface border border-border-subtle rounded-2xl p-6" data-id="element-532">
              <p className="text-xs text-[#7a7672] uppercase tracking-widest mb-6 font-medium text-center" data-id="element-533">
                Arquitectura del sistema
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-0" data-id="element-534">
                {[{
                label: 'Meta / Google Ads',
                sub: 'Tráfico pagado'
              }, null, {
                label: 'Landing Page',
                sub: 'Captura de lead'
              }, null, {
                label: 'GHL CRM',
                sub: 'Central de operaciones'
              }, null, {
                label: 'WhatsApp',
                sub: 'Contacto automático'
              }, null, {
                label: 'Agente',
                sub: 'Conversión'
              }].map((item, i) => item === null ? <div key={i} className="flex items-center" data-id="element-535">
                      <ArrowRightIcon size={14} className="text-accent rotate-90 sm:rotate-0" data-id="element-536" />
                    </div> : <div key={item.label} className="bg-elevated border border-border-subtle rounded-lg px-3 py-2.5 text-center min-w-[100px]" data-id="element-537">
                      <span className="text-xs font-semibold text-cream block" data-id="element-538">
                        {item.label}
                      </span>
                      <span className="text-xs text-[#7a7672]" data-id="element-539">{item.sub}</span>
                    </div>)}
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-540">
        <div className="max-w-3xl mx-auto text-center" data-id="element-541">
          <FadeInSection data-id="element-542">
            <div className="bg-elevated border border-accent/20 rounded-3xl p-10 sm:p-14" data-id="element-543">
              <span className="inline-block w-12 h-1 bg-accent rounded-full mb-6" data-id="element-544" />
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-4" data-id="element-545">
                ¿Quieres ver cómo aplica a tu caso?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed" data-id="element-546">
                Agenda un diagnóstico gratuito. Te mostramos exactamente qué
                piezas necesitas instalar.
              </p>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200 text-base" data-id="element-547">
                Agendar diagnóstico
                <ArrowRightIcon size={18} data-id="element-548" />
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}